import { Logger } from "@classdojo/monsterverse-utils";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import type * as pc from "playcanvas";
import { Component, type ComponentChildren, createRef } from "preact";

import { NotificationUI } from "./NotificationUI";

type RootProps = {
  waitForAppLoad?: boolean;
  children?: ComponentChildren;
};

export class Root extends Component<RootProps> {
  private _ref = createRef();
  private _loads = 0;
  private _loadsNeeded = 0;
  protected app: pc.AppBase;
  private _appReady = false;
  private _appStarted = false;

  override state = { render: false, loadingDone: false };

  constructor() {
    super();

    this.app = window.app;
    this.app.on("appReady", () => {
      this._appReady = true;
      this._checkForReadyToRender();
    });
    this.app.on("start", () => {
      this._appStarted = true;
      this._checkForReadyToRender();
    });
  }

  private _checkForReadyToRender() {
    if (this._appReady && this._appStarted) {
      this.setState({ render: true });
    }
  }

  private _loadingDone() {
    this.setState({ loadingDone: true });
    this.app.fire("uiLoaded");
  }

  private _getAllChildren(children: HTMLCollection, callback: (child: Element) => void) {
    for (const child of children) {
      callback(child);
      this._getAllChildren(child.children, callback);
    }
  }

  override componentDidMount() {
    this._getAllChildren(this._ref.current.children, (child: Element) => {
      if (child instanceof HTMLImageElement) {
        this._loadsNeeded++;
        if (child.complete) {
          this._loads++;
        } else {
          const onLoad = () => {
            this._loads++;
            if (this._loads >= this._loadsNeeded) {
              this._loadingDone();
            }
          };
          child.addEventListener("load", onLoad);
          child.addEventListener("error", () => {
            Logger.logWarn(`Error loading img src for: ${child.outerHTML}`);
            onLoad();
          });
        }
      }
    });

    //Check if we are already done loading (don't have any images or already loaded)
    if (this._loads >= this._loadsNeeded) {
      this._loadingDone();
    }
  }

  render() {
    const theme = createTheme({
      typography: {
        fontFamily: ["OmoType", "Helvetica Neue", "Helvetica", "Arial", "sans-serif"].join(","),
      },
    });

    return (
      <ThemeProvider theme={theme}>
        <div hidden={(this.props.waitForAppLoad && !this.state.render) || !this.state.loadingDone} ref={this._ref}>
          {this.props.children}
        </div>
        <NotificationUI />
      </ThemeProvider>
    );
  }
}

Root.defaultProps = {
  waitForAppLoad: true,
};
